/**
 * Validated Response Schemas
 *
 * Additional Zod schemas for common API response patterns that complement
 * the auto-generated schemas from api-spec.yaml.
 *
 * These schemas wrap the generated schemas to add pagination, lists, etc.
 */

import { z } from 'zod';
import {
  CategorySchema,
  VendorDetailSchema,
  VendorListItemSchema,
  VendorBankSchema,
  VendorContactSchema,
  VendorPaymentSchema,
  PaymentTermSchema,
  TaxTypeSchema,
  UserProfileSchema,
  PaginationSchema,
} from './generated-schemas';

/**
 * Generic paginated list response schema factory
 * Note: data can be null when API returns empty results (e.g., filtered list with no matches)
 */
export function createListResponseSchema<T extends z.ZodType>(itemSchema: T) {
  return z.object({
    data: z.array(itemSchema).nullable(),
    pagination: PaginationSchema,
  });
}

/**
 * Category List Response
 */
export const CategoryListResponseSchema = createListResponseSchema(CategorySchema);
export type CategoryListResponse = z.infer<typeof CategoryListResponseSchema>;

/**
 * Vendor List Response
 */
export const VendorListResponseSchema = createListResponseSchema(VendorListItemSchema);
export type VendorListResponse = z.infer<typeof VendorListResponseSchema>;

/**
 * Vendor Detail Response (alias for consistency)
 */
export const VendorDetailResponseSchema = VendorDetailSchema;
export type VendorDetailResponse = z.infer<typeof VendorDetailResponseSchema>;

/**
 * Category Detail Response (alias for consistency)
 */
export const CategoryDetailResponseSchema = CategorySchema;
export type CategoryDetailResponse = z.infer<typeof CategoryDetailResponseSchema>;

/**
 * Vendor Bank Account Response
 */
export const VendorBankResponseSchema = VendorBankSchema;
export type VendorBankResponse = z.infer<typeof VendorBankResponseSchema>;

/**
 * Vendor Contact Response
 */
export const VendorContactResponseSchema = VendorContactSchema;
export type VendorContactResponse = z.infer<typeof VendorContactResponseSchema>;

/**
 * Vendor Payment Response
 */
export const VendorPaymentResponseSchema = VendorPaymentSchema;
export type VendorPaymentResponse = z.infer<typeof VendorPaymentResponseSchema>;

/**
 * Payment Term List Response
 * Note: This endpoint returns { paymentTerms: [...] } not the standard paginated format
 */
export const PaymentTermListResponseSchema = z.object({
  paymentTerms: z.array(PaymentTermSchema),
});
export type PaymentTermListResponse = z.infer<typeof PaymentTermListResponseSchema>;

/**
 * Payment Term Detail Response
 */
export const PaymentTermDetailResponseSchema = PaymentTermSchema;
export type PaymentTermDetailResponse = z.infer<typeof PaymentTermDetailResponseSchema>;

/**
 * Tax Type List Response
 * Note: This endpoint returns { data: [...] } without pagination
 * Note: data can be null when API returns empty results
 */
export const TaxTypeListResponseSchema = z.object({
  data: z.array(TaxTypeSchema).nullable(),
});
export type TaxTypeListResponse = z.infer<typeof TaxTypeListResponseSchema>;

/**
 * Tax Type Detail Response
 */
export const TaxTypeDetailResponseSchema = TaxTypeSchema;
export type TaxTypeDetailResponse = z.infer<typeof TaxTypeDetailResponseSchema>;

/**
 * User Profile Response
 */
export const UserProfileResponseSchema = UserProfileSchema;
export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>;
